import type {
  NarrativeRule,
  NarrativeRuleInput,
  RuleScope,
  RuleSeverity
} from '@/types';
import {
  normalizeRule
} from '@/utils/storyElementsHelpers';
import { BaseService } from './baseService';

export interface NarrativeRuleValidationResult {
  valid: boolean;
  errors: string[];
}

const defaultSeverity: RuleSeverity = 'strong-guideline';
const defaultScope: RuleScope = 'all-chapters';

class NarrativeRuleService extends BaseService<NarrativeRule> {
  create(input: NarrativeRuleInput): NarrativeRule {
    const now = this.now();

    return normalizeRule({
      id: this.createId('rule'),
      storyElementsId: input.storyElementsId,
      rule: input.rule,
      rationale: input.rationale,
      severity: input.severity ?? defaultSeverity,
      appliesTo: input.appliesTo ?? defaultScope,
      actIds: input.actIds ?? [],
      characterIds: input.characterIds ?? [],
      goodExample: input.goodExample,
      badExample: input.badExample,
      uiState: { isExpanded: false },
      createdAt: now,
      updatedAt: now,
      notes: input.notes ?? ''
    });
  }

  validate(input: Partial<NarrativeRuleInput>): NarrativeRuleValidationResult {
    const errors: string[] = [];

    if (!input.rule || input.rule.trim().length === 0) {
      errors.push('Rule text is required.');
    }

    if (!input.rationale || input.rationale.trim().length === 0) {
      errors.push('Rationale is required.');
    }

    if (input.appliesTo === 'specific-acts' && (!input.actIds || input.actIds.length === 0)) {
      errors.push('Specify the acts for rules scoped to specific acts.');
    }

    if (input.appliesTo === 'specific-characters' && (!input.characterIds || input.characterIds.length === 0)) {
      errors.push('Specify the characters for rules scoped to specific characters.');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  getCommonRules(): ReadonlyArray<NarrativeRuleInput> {
    return COMMON_RULE_TEMPLATES;
  }
}

const COMMON_RULE_TEMPLATES: ReadonlyArray<NarrativeRuleInput> = [
  {
    storyElementsId: '',
    rule: 'Keep chapters in close third-person POV without head hopping.',
    rationale: 'Maintains immersive perspective and emotional continuity.',
    severity: 'hard-rule',
    appliesTo: 'all-chapters',
    goodExample: 'Stay inside the protagonist’s perceptions and thoughts for the entire scene.',
    badExample: 'Swap to another character’s internal thoughts without a scene break.'
  },
  {
    storyElementsId: '',
    rule: 'Use present tense narration throughout.',
    rationale: 'Delivers immediacy and consistent voice.',
    severity: 'hard-rule',
    appliesTo: 'all-chapters',
    goodExample: 'She steps into the room and closes the door.',
    badExample: 'She stepped into the room and closed the door.'
  },
  {
    storyElementsId: '',
    rule: 'Prefer dialogue tags “said” or “asked”.',
    rationale: 'Keeps prose invisible and prevents tonal drift.',
    severity: 'strong-guideline',
    appliesTo: 'all-chapters',
    goodExample: '"We leave at dawn," she said.',
    badExample: '"We leave at dawn," she intoned dramatically.'
  },
  {
    storyElementsId: '',
    rule: 'Show emotional beats through action and sensation.',
    rationale: 'Supports cinematic pacing and avoids exposition.',
    severity: 'strong-guideline',
    appliesTo: 'all-chapters',
    goodExample: 'His grip tightens on the glass until it creaks.',
    badExample: 'He feels extremely angry.'
  },
  {
    storyElementsId: '',
    rule: 'End each chapter on an unresolved beat or forward hook.',
    rationale: 'Maintains suspense and propels readers onward.',
    severity: 'soft-guideline',
    appliesTo: 'all-chapters',
    goodExample: 'The lights go out before she answers the question.',
    badExample: 'Everyone agrees the plan worked and celebrates.',
    notes: 'Soft guideline to encourage pacing discipline.'
  }
];

export const narrativeRuleService = new NarrativeRuleService();
